import {useTranslation} from 'react-i18next';

import {buildResponsiveImageData} from 'Shared/buildResponsiveImageData';
import {usePromo} from 'UI/hooks/usePromo';

import type {Walk} from 'types/payload-types';

const COVER_IMAGE_WIDTH = 600;

/**
 * Custom hook for the `WalkCard` component.
 * Derives the responsive cover image data, city name, and pricing display state from the walk,
 * and provides the translation function.
 *
 * @param walk The Walk document to render.
 * @returns An object containing:
 * - `cityName`: The resolved city name, or an empty string when not populated.
 * - `href`: The detail page link, carrying an active promo code.
 * - `imgData`: The responsive cover image data, or `null` when there is no cover image.
 * - `isFree`: Whether the walk is free.
 * - `promoPrice`: The price display including a possible promo discount.
 * - `t`: The translation function.
 */
export const useWalkCard = (walk: Walk) => {
    const {t} = useTranslation();
    const {getWalkPrice, withPromo} = usePromo();
    const coverImage = typeof walk.coverImage === 'object' ? walk.coverImage : null;
    const imgData = buildResponsiveImageData(coverImage, COVER_IMAGE_WIDTH);
    const cityName = typeof walk.city === 'object' ? walk.city.name : '';
    const isFree = walk.pricing?.isFree !== false;

    return {
        cityName,
        href: withPromo(`/walks/${walk.slug}`),
        imgData,
        isFree,
        promoPrice: getWalkPrice(walk),
        t
    };
};